import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { vkBotApi, type VkBotTrainingExample } from '../lib/vkBotApi';
import { hasPermission, type VkAppUser } from '../lib/authApi';

/**
 * «Обучение» бота:
 *  - Общие знания сети — один текст для ВСЕХ групп (попадает в каждый промпт).
 *  - Примеры-коррекции — «клиент спросил → правильный ответ [→ так нельзя]»,
 *    бот следует им как эталону. Добавляются отсюда или из ленты («Обучить»).
 */
export function TrainingPage({ user }: { user: VkAppUser }): JSX.Element {
  const qc = useQueryClient();
  const canManage = hasPermission(user, 'vk_bot:manage');
  const [knowledge, setKnowledge] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const trainingQuery = useQuery({
    queryKey: ['vk-bot', 'training'],
    queryFn: () => vkBotApi.getTraining(),
  });
  const groupsQuery = useQuery({
    queryKey: ['vk-bot', 'groups'],
    queryFn: () => vkBotApi.listGroups(),
  });

  useEffect(() => {
    if (trainingQuery.data) setKnowledge(trainingQuery.data.globalKnowledge);
  }, [trainingQuery.data]);

  const saveKnowledgeMut = useMutation({
    mutationFn: () => vkBotApi.saveGlobalKnowledge(knowledge),
    onSuccess: async () => {
      void message.success('Общие знания сохранены — действуют для всех групп');
      await qc.invalidateQueries({ queryKey: ['vk-bot', 'training'] });
    },
    onError: (err) =>
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить'),
  });

  const exampleMut = useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof vkBotApi.updateExample>[1] }) =>
      vkBotApi.updateExample(args.id, args.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vk-bot', 'training'] }),
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => vkBotApi.removeExample(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vk-bot', 'training'] }),
  });

  const examples = trainingQuery.data?.examples ?? [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Обучение бота
      </Typography.Title>

      <Card title="Общие знания сети (для ВСЕХ групп)">
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Всё, что верно для любой точки FISHKA: что мы продаём, чего у нас
            НЕТ (например «дегустаций не проводим»), как оформляется заказ,
            стандартные акции. Уникальное для города/группы — в настройках
            самой группы, вкладка «Знания».
          </Typography.Text>
          <Input.TextArea
            rows={10}
            value={knowledge}
            onChange={(e) => setKnowledge(e.target.value)}
            placeholder={'Мы — сеть доставки суши и роллов FISHKA.\nДегустаций НЕ проводим.\nЗаказ: на сайте или по телефону точки.\n…'}
            disabled={!canManage}
          />
          <Button
            type="primary"
            loading={saveKnowledgeMut.isPending}
            disabled={!canManage}
            onClick={() => saveKnowledgeMut.mutate()}
          >
            Сохранить общие знания
          </Button>
        </Space>
      </Card>

      <Card
        title={`Примеры «как отвечать правильно» (${examples.length})`}
        extra={
          canManage && (
            <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
              Добавить пример
            </Button>
          )
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Как это работает"
            description="Бот получает эти примеры в каждом запросе и следует их фактам и стилю. Удобнее всего добавлять из «Ленты ответов»: кнопка «Обучить» на неудачном ответе — комментарий и ответ бота подставятся сами, вам останется написать правильный вариант. В промпт идут 15 последних активных примеров."
          />
          <Table<VkBotTrainingExample>
            rowKey="id"
            size="small"
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            dataSource={examples}
            locale={{ emptyText: 'Примеров пока нет' }}
            columns={[
              {
                title: 'Клиент спросил',
                dataIndex: 'question',
                width: '28%',
                render: (v: string) => <Typography.Text>{v}</Typography.Text>,
              },
              {
                title: 'Правильные ответы',
                dataIndex: 'goodReplies',
                width: '32%',
                render: (v: string[]) => (
                  <Space direction="vertical" size={2}>
                    {v.map((r, i) => (
                      <Typography.Text key={i} style={{ color: '#389e0d', fontSize: 13 }}>
                        {v.length > 1 ? `${i + 1}) ` : ''}
                        {r}
                      </Typography.Text>
                    ))}
                  </Space>
                ),
              },
              {
                title: 'Так нельзя',
                dataIndex: 'badReply',
                width: '24%',
                render: (v: string | null) =>
                  v ? (
                    <Typography.Text delete type="secondary">
                      {v}
                    </Typography.Text>
                  ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                  ),
              },
              {
                title: 'Где',
                dataIndex: 'groupLabel',
                width: 120,
                render: (v: string | null) =>
                  v ? <Tag>{v}</Tag> : <Tag color="blue">все группы</Tag>,
              },
              {
                title: 'Вкл',
                dataIndex: 'isActive',
                width: 70,
                render: (v: boolean, r) => (
                  <Switch
                    size="small"
                    checked={v}
                    disabled={!canManage}
                    onChange={(nv) => exampleMut.mutate({ id: r.id, patch: { isActive: nv } })}
                  />
                ),
              },
              {
                title: '',
                key: 'del',
                width: 50,
                render: (_, r) => (
                  <Popconfirm
                    title="Удалить пример?"
                    onConfirm={() => removeMut.mutate(r.id)}
                    disabled={!canManage}
                  >
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                ),
              },
            ]}
          />
        </Space>
      </Card>

      <AddExampleModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        groups={(groupsQuery.data ?? []).map((g) => ({ value: g.id, label: g.label }))}
      />
    </Space>
  );
}

/** Модалка добавления примера (используется также из ленты — см. FeedPage). */
export function AddExampleModal({
  open,
  onClose,
  groups,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  groups: Array<{ value: string; label: string }>;
  initial?: { question?: string; badReply?: string; groupId?: string | null };
}): JSX.Element {
  const qc = useQueryClient();
  const [question, setQuestion] = useState('');
  const [badReply, setBadReply] = useState('');
  const [variants, setVariants] = useState<string[]>(['']);
  const [groupId, setGroupId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setQuestion(initial?.question ?? '');
      setBadReply(initial?.badReply ?? '');
      setVariants(['']);
      setGroupId(initial?.groupId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filledVariants = variants.map((v) => v.trim()).filter(Boolean);

  const createMut = useMutation({
    mutationFn: () =>
      vkBotApi.createExample({
        groupId,
        question,
        badReply: badReply.trim() || null,
        goodReplies: filledVariants,
      }),
    onSuccess: async () => {
      void message.success('Пример сохранён — бот будет учитывать его в ответах');
      await qc.invalidateQueries({ queryKey: ['vk-bot', 'training'] });
      onClose();
    },
    onError: (err) =>
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить'),
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="🎓 Обучить бота"
      okText="Сохранить пример"
      confirmLoading={createMut.isPending}
      onOk={() => createMut.mutate()}
      okButtonProps={{ disabled: !question.trim() || filledVariants.length === 0 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Typography.Text strong>Клиент спросил / написал</Typography.Text>
          <Input.TextArea
            rows={2}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            style={{ marginTop: 6 }}
          />
        </div>
        <div>
          <Typography.Text strong>Как бот ответил НЕПРАВИЛЬНО (опц.)</Typography.Text>
          <Input.TextArea
            rows={2}
            value={badReply}
            onChange={(e) => setBadReply(e.target.value)}
            style={{ marginTop: 6 }}
            placeholder="Например: приглашение на дегустацию, которой у нас нет"
          />
        </div>
        <div>
          <Typography.Text strong>
            Правильные ответы (бот выбирает и перефразирует, не копирует)
          </Typography.Text>
          <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 6 }}>
            {variants.map((v, idx) => (
              <Space.Compact key={idx} style={{ width: '100%' }}>
                <Input.TextArea
                  rows={2}
                  value={v}
                  placeholder={`Вариант ${idx + 1}`}
                  onChange={(e) =>
                    setVariants((vs) => vs.map((x, i) => (i === idx ? e.target.value : x)))
                  }
                />
                {variants.length > 1 && (
                  <Button
                    danger
                    onClick={() => setVariants((vs) => vs.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </Button>
                )}
              </Space.Compact>
            ))}
            {variants.length < 10 && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => setVariants((vs) => [...vs, ''])}
              >
                Добавить вариант
              </Button>
            )}
          </Space>
        </div>
        <div>
          <Typography.Text strong>Где применять</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            value={groupId ?? '__all__'}
            onChange={(v) => setGroupId(v === '__all__' ? null : v)}
            options={[{ value: '__all__', label: '🌐 Все группы' }, ...groups]}
          />
        </div>
      </Space>
    </Modal>
  );
}
